import React from 'react';
import { Shield, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PasswordStrength = ({ password }) => {
  const { t, i18n } = useTranslation();

  const calculateStrength = (pass) => {
    let score = 0;
    if (!pass) return 0;
    
    // length
    if (pass.length > 5) score += 1;
    if (pass.length > 8) score += 1;
    
    // complexity
    if (/[A-Z]/.test(pass)) score += 1; // has uppercase
    if (/[0-9]/.test(pass)) score += 1; // has number
    if (/[^A-Za-z0-9]/.test(pass)) score += 1; // has special char

    return Math.min(5, score);
  };

  const score = calculateStrength(password);

  const getStrengthLabel = () => {
    if (score === 0) return { label: '', color: 'bg-gray-100', text: 'text-gray-400', icon: null };
    if (score <= 2) return {
      label: t('pwd_strength_weak'),
      color: 'bg-red-400', text: 'text-red-500', icon: ShieldAlert
    };
    if (score <= 3) return {
      label: t('pwd_strength_fair'),
      color: 'bg-amber-400', text: 'text-amber-500', icon: Shield
    };
    if (score <= 4) return {
      label: t('pwd_strength_good'),
      color: 'bg-blue-400', text: 'text-blue-500', icon: ShieldCheck
    };
    return {
      label: t('pwd_strength_strong'),
      color: 'bg-green-500', text: 'text-green-600', icon: ShieldCheck
    };
  };

  const strength = getStrengthLabel();
  const Icon = strength.icon;

  return (
    <div className="w-full mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${strength.text}`}>
          {password && strength.label}
        </span>
        {password && Icon && <Icon className={`w-3.5 h-3.5 ${strength.text}`} />}
      </div>
      <div className="flex gap-1 h-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className={`flex-1 rounded-full transition-all duration-300 ${
              password && level <= score ? strength.color : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default PasswordStrength;
